import React, { useState, useEffect } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Paper, Divider, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { getSupportContact } from '../../Api/ProfileApi';

const SupportHelp = () => {
    const [expanded, setExpanded] = useState(false);
    const [contactInfo, setContactInfo] = useState({
        email: 'support@ronojobs.com',
        phone: '',
        whatsapp: '',
        address: ''
    });
    const [loadingContact, setLoadingContact] = useState(true);

    useEffect(() => {
        const fetchSupportDetails = async () => {
            try {
                setLoadingContact(true);
                const response = await getSupportContact();
                const data = response?.data || response || [];
                // Pick the first support contact record if available
                if (Array.isArray(data) && data.length > 0) {
                    const firstRecord = data[0];
                    setContactInfo({
                        email: firstRecord.email || 'support@ronojobs.com',
                        phone: firstRecord.phone || '',
                        whatsapp: firstRecord.whatsapp || '',
                        address: firstRecord.address || ''
                    });
                }
            } catch (error) {
                console.error('Failed to load support contact details:', error);
            } finally {
                setLoadingContact(false);
            }
        };

        fetchSupportDetails();
    }, []);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const faqs = [
        {
            question: "How do I post a new job listing?",
            answer: "To post a new job listing, navigate to the 'Post A Job' section from the main menu. Fill in all the required details including job title, description, requirements, location, and salary range. Once you've completed the form, click 'Publish' to make your job listing live."
        },
        {
            question: "How can I search for candidates?",
            answer: "You can search for candidates by going to the 'Search Candidate' section. Use the search filters to narrow down candidates by skills, experience level, location, or job title. You can also browse candidate profiles and view their resumes directly from the search results."
        },
        {
            question: "How do I manage interview schedules?",
            answer: "Navigate to the 'Interviews' section to view and manage all your scheduled interviews. You can create new interview appointments, reschedule existing ones, send reminders to candidates, and update interview status. The calendar view helps you see all interviews at a glance."
        },
        {
            question: "Can I edit my profile information?",
            answer: "Yes, you can edit your profile information at any time. Go to 'Profile Settings' and select 'Profile & Security'. From there, you can update your email, phone number, first name, last name, and change your password. Remember to click 'Confirm & Save' to save your changes."
        },
        {
            question: "How do I manage notification preferences?",
            answer: "To manage your notification preferences, go to 'Profile Settings' and select 'Notification Settings'. You can toggle notifications on or off for general notifications, email alerts, and candidate message alerts. Changes are saved automatically."
        },
        {
            question: "What should I do if I forget my password?",
            answer: "If you forget your password, click on the 'Forgot Password' link on the login page. Enter your registered email address, and you'll receive a password reset link. Follow the instructions in the email to create a new password. If you're already logged in, you can change your password from the Profile & Security section."
        },
        {
            question: "How can I contact support for additional help?",
            answer: "If you need additional assistance, you can reach out to our support team through the 'Support & Help' section. You can also email us at support@ronojobs.com or visit our website at www.ronojobs.com. Our support team is available Monday through Friday, 9 AM to 6 PM."
        }
    ];

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary', fontSize: { xs: '20px', md: '24px' } }}>
                Support & Help
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {faqs.map((faq, index) => {
                    const panelId = `panel${index}`;
                    return (
                        <Accordion key={index} expanded={expanded === panelId} onChange={handleChange(panelId)} elevation={0}
                            sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: '8px !important',
                                '&:before': { display: 'none' }, '&.Mui-expanded': { margin: '0 !important' }
                            }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                                sx={{
                                    px: 2.5, py: 1.5, '& .MuiAccordionSummary-content': { my: 0 },
                                    '&.Mui-expanded': { minHeight: '48px', borderBottom: '1px solid', borderColor: 'divider' }
                                }}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                    {faq.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, py: 2, pt: 2 }}>
                                <Typography variant="body2"
                                    sx={{ color: 'text.secondary', fontSize: '14px', lineHeight: 1.6 }}>
                                    {faq.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>

            {/* Contact Information */}
            <Box sx={{ mt: 4 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'text.primary',
                        fontSize: '18px'
                    }}
                >
                    Contact Information
                </Typography>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper'
                    }}
                >
                    {loadingContact ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Email */}
                            {contactInfo.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <EmailOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>
                                            Email
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 500 }}>
                                            {contactInfo.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Phone */}
                            {contactInfo.phone && (
                                <>
                                    <Divider />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <PhoneOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>
                                                Phone Number
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 500 }}>
                                                {contactInfo.phone}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {/* WhatsApp */}
                            {contactInfo.whatsapp && (
                                <>
                                    <Divider />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>
                                                WhatsApp
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 500 }}>
                                                {contactInfo.whatsapp}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {/* Address */}
                            {contactInfo.address && (
                                <>
                                    <Divider />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <LocationOnOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>
                                                Address
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 500 }}>
                                                {contactInfo.address}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            <Divider />
                            {/* Default Website Link */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <LanguageOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mb: 0.5 }}>
                                        Website
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        component="a"
                                        href="https://www.ronojobs.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: 'primary.main',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        www.ronojobs.com
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default SupportHelp;
